import { PopupButtonType, showPopup, Touchable } from "@kalyzee/kast-app-web-components";
import React, { useImperativeHandle, useRef } from "react";
import { useRender } from "../../hooks/component";
import { useElementSize } from "../../hooks/window";
import { OvenMediaTrackVideo } from "../../interfaces/context";
import Table, { TableColumnType, TableConf, TableConfColumn, TableContentRef, TableStyle } from "../utils/Table";
import styles from "./table-video-track.module.css";

export interface TableVideoTrackData extends OvenMediaTrackVideo {}

export interface TableVideoTrackRef {
  render: () => void;
}

export interface TableVideoTrackProps {
  data: TableVideoTrackData[];
  onItemChecked?: (item: TableVideoTrackData) => void;
  className?: string;
  style?: React.CSSProperties;
}
const TableVideoTrack = React.forwardRef(
  ({ data, onItemChecked, className, style }: TableVideoTrackProps, forwardRef: React.ForwardedRef<TableVideoTrackRef | undefined>) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const render = useRender();
    const size = useElementSize(containerRef);

    useImperativeHandle(forwardRef, () => ({
      render,
    }));

    const generateConfiguration = () => {
      const columnConfiguration: TableConfColumn<TableVideoTrackData>[] = [
        /* {
        type: TableColumnType.CHECKBOX,
        key: 'checked',
      }, */
        {
          type: TableColumnType.CLASSIC,
          key: "name",
          minWidth: "8rem",
          title: "Name" /* TRANSLATION */,
          header: { className: styles.tableHeaderCellName },
          item: { className: styles.tableCellName },
        },
        {
          type: TableColumnType.CLASSIC,
          key: "bypass",
          title: "Bypass" /* TRANSLATION */,
          width: "3.5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "codec",
          title: "Codec" /* TRANSLATION */,
          width: "4rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "height",
          title: "Height" /* TRANSLATION */,
          width: "4rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "width",
          title: "Width" /* TRANSLATION */,
          width: "4rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "bitrate",
          title: "Bitrate" /* TRANSLATION */,
          width: "5rem",
        },
        {
          type: TableColumnType.CLASSIC,
          key: "framerate",
          title: "Framerate" /* TRANSLATION */,
          width: "5rem",
        },
      ];

      const tableConfiguration: TableConf<TableVideoTrackData> = {
        columns: columnConfiguration,
        header: {
          className: styles.tableHeader,
          cell: {
            className: styles.tableHeaderCell,
          },
        },
        row: {
          className: styles.tableRow,
          cell: {
            className: styles.tableRowCell,
          },
        },
        content: { className: styles.tableContent },
        valueToShowIfUndefined: { value: "-", className: styles.tableUndefinedValue },
      };

      return tableConfiguration;
    };

    // Called when a value is changed. Checkboxes here
    const valueChanged = (value: any, columnKey: string, item: TableVideoTrackData) => {
      if (columnKey === "checked") onItemChecked?.(item);
    };

    const customRenderCell = (element: JSX.Element | null, elementRef: TableContentRef, columnKey: string, item: TableVideoTrackData) => {
      if (columnKey === "bypass") {
        if (item.video.bypass) {
          return <div className={styles.bypassYes}>✔</div>;
        }
        return <div className={styles.bypassFalse}>✗</div>;
      }
      if (columnKey === "bitrate" && item.video.bitrate) {
        const bitrate = Number(item.video.bitrate ?? "0");
        const bps = Number.isNaN(bitrate) ? 0 : bitrate;
        const kbps = (bps / 1000).toFixed(2);
        const mbits = (bps / 1000000).toFixed(2);
        return (
          <Touchable
            className={styles.bitrate}
            onPress={() => {
              showPopup(
                {
                  content: (
                    <div style={{ minWidth: 300 }}>
                      <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Bitrate:" /* TRANSLATION */}</div>
                      <div>
                        <div style={{ display: "flex", flexDirection: "row", gap: 10, justifyContent: "space-between" }}>
                          <div>bits/s: </div>
                          <div style={{}}>{`${bps} bits/s`}</div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row", gap: 10, justifyContent: "space-between" }}>
                          <div>kbits/s: </div>
                          <div style={{}}>{`${kbps} kbits/s`}</div>
                        </div>
                        <div style={{ display: "flex", flexDirection: "row", gap: 10, justifyContent: "space-between" }}>
                          <div>Mbits/s: </div>
                          <div style={{}}>{`${mbits} Mbits/s`}</div>
                        </div>
                      </div>
                    </div>
                  ),
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                },
                undefined,
                { displayHover: true }
              );
            }}
          >
            {mbits}
          </Touchable>
        );
      }
      return element;
    };

    const addCustomStyleOnCell = (columnKey: string, item: TableVideoTrackData) => {
      const result: TableStyle = {};
      return result;
    };

    const addCustomStyleOnRow = (item: TableVideoTrackData, currData: TableVideoTrackData[], index: number) => {
      const result: TableStyle = {};
      return result;
    };

    const onRenderTableStarts = () => {};

    const onRenderTableEnded = () => {};

    const transformValue = (columnKey: string, item: TableVideoTrackData, initialValue: any, data: TableVideoTrackData[], index: number) => {
      return (item.video as any)?.[columnKey];
    };

    const renderTable = () => (
      <Table
        className={styles.table}
        data={data}
        keyExtractor={(_, item) => `key-${item.name}`}
        configuration={generateConfiguration()}
        onRenderCellRow={customRenderCell}
        onStyleCellRow={addCustomStyleOnCell}
        onStyleRow={addCustomStyleOnRow}
        onChangeValue={valueChanged}
        onRenderStarts={() => onRenderTableStarts}
        onRenderEnded={onRenderTableEnded}
        transformValue={transformValue}
      />
    );

    const classes = [styles.container];
    if (className) classes.push(className);
    return (
      <div className={classes.join(" ")} style={style} ref={containerRef}>
        {renderTable()}
      </div>
    );
  }
);

export default TableVideoTrack;
